import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { Helmet } from 'react-helmet'
import SEO from '../seo'
import apiClient from '../../axios'
import { baseURL, ENDPOINTS } from '../../constants'
import cullectorLogo from '../../assets/images/cullector-logo.png'
import './cullector.css'

// Shared chrome for the Cullector content hub, matching the cullector-handoff
// design (utility bar, header, newsletter band, category rail, footer). All
// styling lives in cullector.css, scoped under the .cullector-hub wrapper.

export interface EditorialCategoryNode {
  id: number
  name: string
  slug: string
  children?: EditorialCategoryNode[]
}

// Active editorial categories, fetched once per session and shared between the
// header/footer/rail and the /cullector/:slug category-vs-article resolver.
// Rejects on network failure (and clears the memo so a later call can retry) —
// callers decide their fallback.
let categoriesPromise: Promise<EditorialCategoryNode[]> | null = null
export const getActiveEditorialCategories = (): Promise<EditorialCategoryNode[]> => {
  if (!categoriesPromise) {
    categoriesPromise = axios
      .get(`${baseURL}/api/v1/editorial-categories/active`)
      .then(res => (Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(err => {
        categoriesPromise = null
        throw err
      })
  }
  return categoriesPromise
}

const useEditorialCategories = (): EditorialCategoryNode[] => {
  const [categories, setCategories] = useState<EditorialCategoryNode[]>([])
  useEffect(() => {
    let cancelled = false
    getActiveEditorialCategories()
      .then(cats => {
        if (!cancelled) setCategories(cats)
      })
      .catch(() => {
        /* chrome is non-critical */
      })
    return () => {
      cancelled = true
    }
  }, [])
  return categories
}

// Spectral + DM Sans, per the brand style guide. Loaded only on hub pages.
export const CullectorFonts = () => (
  <Helmet>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link
      rel="preconnect"
      href="https://fonts.gstatic.com"
      crossOrigin="anonymous"
    />
    <link
      href="https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,300;0,400;0,700;1,300&family=DM+Sans:wght@300;400;500&display=swap"
      rel="stylesheet"
    />
  </Helmet>
)

export const Loading = () => <div className="hub-loading">Loading…</div>

const SearchIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

export const CULLECTOR_SEARCH_PATH = '/cullector/search'

// Header search, per the handoff: a search icon (one per breakpoint, both driving the
// same panel) expands a plain GET form to /cullector/search?q=. Submitting is a normal
// navigation — the results route lives under the same client-only /cullector/* page.
// `searchQuery` (set by the results page) opens the panel prefilled so the query is
// editable in place.
export const CullectorHeader: React.FC<{
  activeSlug?: string
  searchQuery?: string
}> = ({ activeSlug, searchQuery }) => {
  const categories = useEditorialCategories()
  const [navOpen, setNavOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(searchQuery !== undefined)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const toggleSearch = () => {
    setSearchOpen(open => {
      if (!open) {
        // Focus after the panel renders (it's display:none while closed).
        window.setTimeout(() => searchInputRef.current?.focus(), 0)
      }
      return !open
    })
  }

  const searchToggleProps = {
    type: 'button' as const,
    'aria-label': 'Search',
    'aria-expanded': searchOpen,
    'aria-controls': 'cullector-search-panel',
    onClick: toggleSearch,
  }

  return (
    <>
      <div className="utility-bar">
        <div className="wrap">
          <span>Product reviews and buying guides</span>
          <a className="chirp-link" href="/">
            Powered by Chirpyest
          </a>
        </div>
      </div>
      <header className="site-header">
        <div className="wrap header-row">
          <div className="header-top">
            <div className="wordmark-block">
              <a href="/cullector">
                <img
                  className="logo-lockup"
                  src={cullectorLogo}
                  alt="cullector — the best, curated"
                />
              </a>
            </div>
            <div className="mobile-icons">
              <button
                {...searchToggleProps}
                className="search-toggle search-toggle--mobile"
              >
                <SearchIcon />
              </button>
              <button
                type="button"
                className={`menu-toggle${navOpen ? ' is-active' : ''}`}
                aria-label="Menu"
                aria-expanded={navOpen}
                aria-controls="cullector-primary-nav"
                onClick={() => setNavOpen(open => !open)}
              >
                <span />
                <span />
                <span />
              </button>
            </div>
          </div>
          <div className="nav-search-group">
            <nav
              className={`primary-nav${navOpen ? ' open' : ''}`}
              id="cullector-primary-nav"
              aria-label="Primary"
            >
              {categories.map(cat => (
                <a
                  key={cat.id}
                  href={`/cullector/${cat.slug}`}
                  className={activeSlug === cat.slug ? 'active' : undefined}
                >
                  {cat.name}
                </a>
              ))}
            </nav>
            <button
              {...searchToggleProps}
              className="search-toggle search-toggle--desktop"
            >
              <SearchIcon />
            </button>
          </div>
        </div>
        <div className="wrap">
          <form
            className={`search-panel${searchOpen ? ' open' : ''}`}
            id="cullector-search-panel"
            role="search"
            action={CULLECTOR_SEARCH_PATH}
            method="get"
            onSubmit={e => {
              // Let the browser navigate on a real query; just swallow blanks.
              if (!searchInputRef.current?.value.trim()) {
                e.preventDefault()
                searchInputRef.current?.focus()
              }
            }}
          >
            <input
              ref={searchInputRef}
              type="search"
              name="q"
              defaultValue={searchQuery || ''}
              placeholder="Search guides…"
              aria-label="Search guides"
              autoComplete="off"
              onKeyDown={e => {
                if (e.key === 'Escape' && searchQuery === undefined) {
                  setSearchOpen(false)
                }
              }}
            />
            <button type="submit">Search</button>
          </form>
        </div>
      </header>
    </>
  )
}

export const NewsletterBand = () => {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sent' | 'error'>('idle')

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      // `source: 'cullector'` routes the address into the Cullector Klaviyo list
      // on the backend as well as the local newsletter table.
      await apiClient.post(ENDPOINTS.newsLetter, { email, source: 'cullector' })
      setEmail('')
      setStatus('sent')
    } catch {
      setStatus('error')
    }
  }

  return (
    <section className="newsletter" aria-label="Newsletter signup">
      <div className="wrap newsletter-row">
        <div>
          <h2 className="t-h2">Get the pick, not the pitch</h2>
          <p className="t-body">
            One email a week. The guides worth reading, nothing else.
          </p>
        </div>
        <form className="newsletter-form" onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="you@email.com"
            aria-label="Email address"
            required
            value={email}
            onChange={event => {
              setEmail(event.target.value)
              setStatus('idle')
            }}
          />
          <button type="submit">Sign up</button>
          {status === 'sent' && (
            <p className="newsletter-msg">
              You're signed up. Watch your inbox.
            </p>
          )}
          {status === 'error' && (
            <p className="newsletter-msg error">
              Something went wrong — please try again.
            </p>
          )}
        </form>
      </div>
    </section>
  )
}

// Crawlable sitemap rail of every active category, including ones without a
// landing section yet.
export const CategoryRail = () => {
  const categories = useEditorialCategories()
  if (categories.length === 0) return null
  return (
    <section className="category-rail-section" aria-label="All categories">
      <div className="wrap">
        <nav className="category-rail" aria-label="All categories">
          {categories.map(cat => (
            <a key={cat.id} href={`/cullector/${cat.slug}`}>
              {cat.name}
            </a>
          ))}
        </nav>
      </div>
    </section>
  )
}

export const CullectorFooter = () => {
  const categories = useEditorialCategories()
  return (
    <footer className="site-footer">
      <div className="wrap">
        <div className="footer-top">
          <div>
            <a href="/cullector">
              <img
                className="footer-logo"
                src={cullectorLogo}
                alt="cullector — the best, curated"
              />
            </a>
          </div>
          <div className="footer-cols">
            {categories.length > 0 && (
              <div className="footer-col">
                <h4>Guides</h4>
                {categories.map(cat => (
                  <a key={cat.id} href={`/cullector/${cat.slug}`}>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
            <div className="footer-col">
              <h4>Company</h4>
              <a href="/about">About</a>
            </div>
            <div className="footer-col">
              <h4>Legal</h4>
              <a href="/company#terms">Terms</a>
              <a href="/company#privacy">Privacy</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <span>
            © Cullector {new Date().getFullYear()}. All rights reserved.
          </span>
          <span>
            Cullector may earn a commission from products bought through our
            links.
          </span>
        </div>
      </div>
    </footer>
  )
}

export const PageShell: React.FC<{
  children: React.ReactNode
  title: string
  description?: string
  activeSlug?: string
  belowNewsletter?: React.ReactNode
  // Results pages open the header search prefilled with the active query.
  searchQuery?: string
  noindex?: boolean
}> = ({
  children,
  title,
  description,
  activeSlug,
  belowNewsletter,
  searchQuery,
  noindex,
}) => (
  <>
    <SEO title={title} description={description} noindex={noindex} />
    <CullectorFonts />
    <div className="cullector-hub">
      <CullectorHeader activeSlug={activeSlug} searchQuery={searchQuery} />
      <main>{children}</main>
      <NewsletterBand />
      {belowNewsletter}
      <CullectorFooter />
    </div>
  </>
)
